import { ChangeDetectionStrategy, Component, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';

import { RouterModule } from '@angular/router';
import { FooterComponent } from "../../shared/components/footer/footer.component";
import { SidebarComponent } from "../../shared/components/sidebar/sidebar.component";
import { AuthService } from '../../core/services/auth';
import { MatIconModule } from '@angular/material/icon';
import { filter } from 'rxjs/operators';

/** localStorage key that remembers whether the desktop menu was left collapsed. */
export const SIDEBAR_COLLAPSED_KEY = 'bro.sidebar.collapsed';

@Component({
  selector: 'app-private-layout',
  standalone: true,
  imports: [RouterOutlet, RouterModule, FooterComponent, SidebarComponent, MatIconModule],
  templateUrl: './private-layout.component.html',
  styleUrls: ['./private-layout.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PrivateLayoutComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly userName = computed(() => this.authService.currentUser()?.name ?? 'Usuario');
  /** Mobile drawer open/closed. */
  readonly isSidebarOpen = signal(false);
  /** Desktop menu collapsed to an icon rail; remembered between visits. */
  readonly isSidebarCollapsed = signal(this.readCollapsedPreference());

  constructor() {
    // Cerrar sidebar al navegar en móvil
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.isSidebarOpen.set(false);
    });
  }

  toggleSidebar() {
    this.isSidebarOpen.update(v => !v);
  }

  toggleSidebarCollapsed() {
    const next = !this.isSidebarCollapsed();
    this.isSidebarCollapsed.set(next);
    this.storeCollapsedPreference(next);
  }

  /** Storage can be unavailable (private mode, blocked site data): the menu then simply starts expanded. */
  private readCollapsedPreference(): boolean {
    if (!this.isBrowser) return false;
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private storeCollapsedPreference(collapsed: boolean): void {
    if (!this.isBrowser) return;
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
      /* the preference just won't persist */
    }
  }

  logout() {
    this.authService.logout();
  }
}
